import { BUTTON_EDGE_OFFSET } from '../../../contants';
import type { EmbedIconCorner, IconOffset } from '../../../types';

export const DEFAULT_ICON_CORNER: EmbedIconCorner = 'bottomRight';
export const DEFAULT_LAUNCHER_Z_INDEX = 9999;
export const MAX_ICON_OFFSET = 500;

const VALID_ICON_CORNERS: EmbedIconCorner[] = ['bottomRight', 'bottomLeft', 'topRight', 'topLeft'];

export type IconCornerStyles = {
  top: string;
  right: string;
  bottom: string;
  left: string;
  flexDirection: 'column' | 'column-reverse';
};

/**
 * Normalizes an icon corner value from assistantConfig. Falls back to bottomRight when invalid.
 */
export const normalizeIconCorner = (value: unknown): EmbedIconCorner => {
  if (typeof value === 'string' && VALID_ICON_CORNERS.includes(value as EmbedIconCorner)) {
    return value as EmbedIconCorner;
  }
  return DEFAULT_ICON_CORNER;
};

/**
 * Normalizes icon offset values from assistantConfig. Ignores invalid, negative, and oversized values.
 */
export const normalizeIconOffset = (value: unknown): IconOffset => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const input = value as Record<string, unknown>;
  const edges = ['top', 'right', 'bottom', 'left'] as const;
  const result: IconOffset = {};

  for (const edge of edges) {
    const candidate = input[edge];
    if (typeof candidate === 'number' && !Number.isNaN(candidate) && candidate >= 0) {
      result[edge] = Math.min(candidate, MAX_ICON_OFFSET);
    }
  }

  return result;
};

/**
 * Normalizes launcher z-index from assistantConfig. Falls back to DEFAULT_LAUNCHER_Z_INDEX when invalid.
 */
export const normalizeLauncherZIndex = (value: unknown): number => {
  if (typeof value === 'number' && !Number.isNaN(value) && value >= 0) {
    return Math.min(Math.floor(value), 2147483647);
  }
  return DEFAULT_LAUNCHER_Z_INDEX;
};

const applyOffsetToInset = (inset: string, extraPx: number | undefined): string => {
  if (inset === 'auto' || extraPx === undefined || extraPx === 0) {
    return inset;
  }

  const base = Number.parseInt(inset, 10) || 0;
  return `${base + extraPx}px`;
};

const getBaseIconCornerStyles = (corner: EmbedIconCorner, isSmallDevice: boolean): IconCornerStyles => {
  const offset = isSmallDevice ? '0' : `${BUTTON_EDGE_OFFSET}px`;

  switch (corner) {
    case 'bottomLeft':
      return {
        top: 'auto',
        right: 'auto',
        bottom: offset,
        left: offset,
        flexDirection: 'column',
      };
    case 'topRight':
      return {
        top: offset,
        right: offset,
        bottom: 'auto',
        left: 'auto',
        flexDirection: 'column-reverse',
      };
    case 'topLeft':
      return {
        top: offset,
        right: 'auto',
        bottom: 'auto',
        left: offset,
        flexDirection: 'column-reverse',
      };
    case 'bottomRight':
    default:
      return {
        top: 'auto',
        right: offset,
        bottom: offset,
        left: 'auto',
        flexDirection: 'column',
      };
  }
};

/**
 * Returns fixed-position inset and flex direction for the embed launcher container.
 */
export const getIconCornerStyles = (
  corner: EmbedIconCorner,
  isSmallDevice: boolean,
  iconOffset: IconOffset = {},
): IconCornerStyles => {
  const base = getBaseIconCornerStyles(corner, isSmallDevice);

  return {
    top: applyOffsetToInset(base.top, iconOffset.top),
    right: applyOffsetToInset(base.right, iconOffset.right),
    bottom: applyOffsetToInset(base.bottom, iconOffset.bottom),
    left: applyOffsetToInset(base.left, iconOffset.left),
    flexDirection: base.flexDirection,
  };
};
